import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Plus, Edit2, Trash2, Clock, DollarSign, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WEEKDAYS = [
  { value: 0, short: 'Sen', label: 'Senin' }, { value: 1, short: 'Sel', label: 'Selasa' },
  { value: 2, short: 'Rab', label: 'Rabu' }, { value: 3, short: 'Kam', label: 'Kamis' },
  { value: 4, short: 'Jum', label: 'Jumat' }, { value: 5, short: 'Sab', label: 'Sabtu' },
  { value: 6, short: 'Min', label: 'Minggu' }
];

export default function PaketManagement() {
  const [packages, setPackages] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [packagesRes, branchesRes] = await Promise.all([
        axios.get(`${API_URL}/api/packages?include_unavailable=true`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/branches`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      setPackages(packagesRes.data || []);
      if (branchesRes.data.success) {
        setBranches(branchesRes.data.branches);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Gagal memuat data paket');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (pkg) => {
    setSelectedPackage(pkg);
    setShowEditModal(true);
  };

  const handleDeleteClick = (pkg) => {
    setSelectedPackage(pkg);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!selectedPackage) return;

    try {
      await axios.delete(`${API_URL}/api/packages/${selectedPackage.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Paket berhasil dihapus');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus paket');
    } finally {
      setSelectedPackage(null);
    }
  };

  const getBranchName = (pkg) => {
    if (pkg.branch_id) {
      const branch = branches.find(b => b.branch_id === pkg.branch_id);
      if (branch) return branch.name;
    }
    if (pkg.branch) return pkg.branch;
    return 'Semua Cabang';
  };

  const formatDuration = (hours) => {
    if (hours >= 1) {
      const wholeHours = Math.floor(hours);
      const minutes = Math.round((hours - wholeHours) * 60);
      if (minutes > 0) {
        return `${wholeHours} jam ${minutes} menit`;
      }
      return `${wholeHours} jam`;
    } else {
      const minutes = Math.round(hours * 60);
      return `${minutes} menit`;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Kelola Paket Rental</h2>
          <p className="text-sm text-gray-600">Buat dan kelola paket rental per cabang</p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-green-600 hover:bg-green-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Tambah Paket
        </Button>
      </div>

      {/* Packages Grid */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Memuat data...</p>
        </div>
      ) : packages.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg mb-2">Belum ada paket rental</p>
          <p className="text-gray-400 text-sm mb-4">Buat paket rental untuk memudahkan kasir</p>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Tambah Paket Pertama
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <Card key={pkg.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">{pkg.name}</CardTitle>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      pkg.ps_type === 'PS5' ? 'bg-purple-100 text-purple-700' :
                      pkg.ps_type === 'PS4' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {pkg.ps_type}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditClick(pkg)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteClick(pkg)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-700 font-medium">
                    {formatDuration(pkg.duration_hours)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="h-4 w-4 text-gray-500" />
                  <span className="text-lg font-bold text-green-600">
                    {formatCurrency(pkg.price)}
                  </span>
                </div>
                <div className="pt-2 border-t">
                  <p className="text-xs text-gray-500">Cabang:</p>
                  <p className="text-sm font-medium text-gray-700">{getBranchName(pkg)}</p>
                </div>
                <div className="pt-2 border-t flex items-start gap-2">
                  <CalendarDays className="h-4 w-4 text-cyan-400 mt-0.5" />
                  {pkg.availability_type === 'scheduled' ? (
                    <div><p className="text-sm font-semibold text-cyan-300">Promo terjadwal</p>
                      <p className="text-xs text-gray-400">{(pkg.promo_days || []).map(day => WEEKDAYS.find(item => item.value === day)?.short).join(', ')} · {pkg.promo_start_time}–{pkg.promo_end_time} WIB</p>
                      <span className={`inline-flex mt-2 rounded-full px-2 py-1 text-[11px] font-semibold ${pkg.is_available_now ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-500/15 text-slate-400'}`}>{pkg.is_available_now ? 'Sedang aktif' : 'Di luar jadwal'}</span>
                    </div>
                  ) : <p className="text-sm text-gray-400">Berlaku setiap hari & jam</p>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <AddPackageModal
          branches={branches}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchData();
          }}
          token={token}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPackage && (
        <EditPackageModal
          package={selectedPackage}
          branches={branches}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPackage(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedPackage(null);
            fetchData();
          }}
          token={token}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSelectedPackage(null);
        }}
        onConfirm={handleDeleteConfirmed}
        title="Konfirmasi Hapus Paket"
        description={`Apakah Anda yakin ingin menghapus paket "${selectedPackage?.name}"?`}
        confirmText="Ya, Hapus"
        cancelText="Batal"
        variant="danger"
      />
    </div>
  );
}

// Add Package Modal Component
function AddPackageModal({ branches, onClose, onSuccess, token }) {
  const [formData, setFormData] = useState({
    name: '',
    ps_type: '',
    duration_hours: '',
    price: '',
    branch_id: '',
    branch: '', availability_type: 'lifetime', promo_days: [], promo_start_time: '10:00', promo_end_time: '17:00'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (formData.availability_type === 'scheduled' && formData.promo_days.length === 0) throw new Error('Pilih minimal satu hari promo');
      const payload = {
        ...formData,
        duration_hours: parseFloat(formData.duration_hours),
        price: parseInt(formData.price)
      };

      const response = await axios.post(
        `${API_URL}/api/packages`,
        payload,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data) {
        toast.success('Paket berhasil ditambahkan');
        onSuccess();
      }
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Gagal menambahkan paket';
      setError(message); toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tambah Paket Rental Baru</DialogTitle>
          <DialogDescription>
            Buat paket rental dengan harga dan durasi khusus
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Nama Paket *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Contoh: Paket Hemat PS5 3 Jam"
              required
            />
          </div>

          <div>
            <Label htmlFor="ps_type">Tipe PS *</Label>
            <select
              id="ps_type"
              value={formData.ps_type}
              onChange={(e) => setFormData({...formData, ps_type: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">-- Pilih Tipe PS --</option>
              <option value="PS3">PS3</option>
              <option value="PS4">PS4</option>
              <option value="PS5">PS5</option>
            </select>
          </div>

          <div>
            <Label htmlFor="duration_hours">Durasi (Jam) *</Label>
            <Input
              id="duration_hours"
              type="number"
              step="0.25"
              value={formData.duration_hours}
              onChange={(e) => setFormData({...formData, duration_hours: e.target.value})}
              placeholder="Contoh: 3 atau 1.5"
              required
              min="0.25"
            />
            <p className="text-xs text-gray-500 mt-1">
              Bisa dalam jam (3) atau desimal (1.5 = 1 jam 30 menit, 0.5 = 30 menit)
            </p>
          </div>

          <div>
            <Label htmlFor="price">Harga Paket (Rp) *</Label>
            <Input
              id="price"
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({...formData, price: e.target.value})}
              placeholder="Contoh: 30000"
              required
              min="0"
            />
          </div>

          <div>
            <Label htmlFor="branch">Cabang *</Label>
            <select
              id="branch"
              value={formData.branch_id}
              onChange={(e) => {
                const selectedBranch = branches.find(b => b.branch_id === e.target.value);
                setFormData({
                  ...formData, 
                  branch_id: e.target.value,
                  branch: selectedBranch?.name || ''
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Pilih Cabang</option>
              {branches.map(branch => (
                <option key={branch.branch_id} value={branch.branch_id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Paket hanya berlaku untuk cabang yang dipilih
            </p>
          </div>
          <ScheduleFields formData={formData} setFormData={setFormData} />

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Batal
            </Button>
            <Button 
              type="submit" 
              disabled={loading} 
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? 'Menyimpan...' : 'Tambah Paket'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Edit Package Modal Component
function EditPackageModal({ package: pkg, branches, onClose, onSuccess, token }) {
  const [formData, setFormData] = useState({
    name: pkg.name || '',
    ps_type: pkg.ps_type || '',
    duration_hours: pkg.duration_hours || '',
    price: pkg.price || '',
    branch_id: pkg.branch_id || '',
    branch: pkg.branch || '', availability_type: pkg.availability_type || 'lifetime', promo_days: pkg.promo_days || [], promo_start_time: pkg.promo_start_time || '10:00', promo_end_time: pkg.promo_end_time || '17:00'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (formData.availability_type === 'scheduled' && formData.promo_days.length === 0) throw new Error('Pilih minimal satu hari promo');
      const payload = {
        ...formData,
        duration_hours: parseFloat(formData.duration_hours),
        price: parseInt(formData.price)
      };

      const response = await axios.put(
        `${API_URL}/api/packages/${pkg.id}`,
        payload,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data) {
        toast.success('Paket berhasil diupdate');
        onSuccess();
      }
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Gagal mengupdate paket';
      setError(message); toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Paket Rental</DialogTitle>
          <DialogDescription>
            Update informasi paket rental
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Nama Paket *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Contoh: Paket Hemat PS5 3 Jam"
              required
            />
          </div>

          <div>
            <Label htmlFor="ps_type">Tipe PS *</Label>
            <select
              id="ps_type"
              value={formData.ps_type}
              onChange={(e) => setFormData({...formData, ps_type: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">-- Pilih Tipe PS --</option>
              <option value="PS3">PS3</option>
              <option value="PS4">PS4</option>
              <option value="PS5">PS5</option>
            </select>
          </div>

          <div>
            <Label htmlFor="duration_hours">Durasi (Jam) *</Label>
            <Input
              id="duration_hours"
              type="number"
              step="0.25"
              value={formData.duration_hours}
              onChange={(e) => setFormData({...formData, duration_hours: e.target.value})}
              placeholder="Contoh: 3 atau 1.5"
              required
              min="0.25"
            />
            <p className="text-xs text-gray-500 mt-1">
              Bisa dalam jam (3) atau desimal (1.5 = 1 jam 30 menit)
            </p>
          </div>

          <div>
            <Label htmlFor="price">Harga Paket (Rp) *</Label>
            <Input
              id="price"
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({...formData, price: e.target.value})}
              placeholder="Contoh: 30000"
              required
              min="0"
            />
          </div>

          <div>
            <Label htmlFor="branch">Cabang *</Label>
            <select
              id="branch"
              value={formData.branch_id}
              onChange={(e) => {
                const selectedBranch = branches.find(b => b.branch_id === e.target.value);
                setFormData({
                  ...formData, 
                  branch_id: e.target.value,
                  branch: selectedBranch?.name || ''
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Pilih Cabang</option>
              {branches.map(branch => (
                <option key={branch.branch_id} value={branch.branch_id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
          <ScheduleFields formData={formData} setFormData={setFormData} />

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Batal
            </Button>
            <Button 
              type="submit" 
              disabled={loading} 
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleFields({ formData, setFormData }) {
  const toggleDay = (day) => setFormData({...formData, promo_days: formData.promo_days.includes(day) ? formData.promo_days.filter(value => value !== day) : [...formData.promo_days, day].sort((a, b) => a - b)});
  return (
    <div className="package-schedule-panel rounded-xl p-4 space-y-4">
      <div><Label>Ketersediaan Paket</Label><div className="grid grid-cols-2 gap-2 mt-2">
        <button type="button" onClick={() => setFormData({...formData, availability_type: 'lifetime'})} className={`package-schedule-mode ${formData.availability_type === 'lifetime' ? 'is-active' : ''}`}>Setiap Waktu</button>
        <button type="button" onClick={() => setFormData({...formData, availability_type: 'scheduled'})} className={`package-schedule-mode ${formData.availability_type === 'scheduled' ? 'is-active' : ''}`}>Promo Terjadwal</button>
      </div></div>
      {formData.availability_type === 'scheduled' && <>
        <div><Label>Hari Promo *</Label><div className="grid grid-cols-4 gap-2 mt-2">{WEEKDAYS.map(day => <button key={day.value} type="button" title={day.label} onClick={() => toggleDay(day.value)} className={`package-schedule-day ${formData.promo_days.includes(day.value) ? 'is-active' : ''}`}>{day.short}</button>)}</div></div>
        <div className="grid grid-cols-2 gap-3"><div><Label htmlFor="promo_start_time">Mulai (WIB) *</Label><Input id="promo_start_time" type="time" value={formData.promo_start_time} onChange={(e) => setFormData({...formData, promo_start_time: e.target.value})} required /></div><div><Label htmlFor="promo_end_time">Selesai (WIB) *</Label><Input id="promo_end_time" type="time" value={formData.promo_end_time} onChange={(e) => setFormData({...formData, promo_end_time: e.target.value})} required /></div></div>
        <p className="text-xs text-slate-400">Bisa melewati tengah malam, misalnya 22:00–02:00.</p>
      </>}
    </div>
  );
}
